import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import useSWRImmutable from 'swr/immutable';
import settings from '/@/core/settings';
import { _ } from '/@/locale';
import { axFetch } from '/@/store/session/actions';
import { getAppInstance, getSessionCountry } from '/@/store/session/selectors';
import type { CropType } from '/@/types/crop-monitoring';
import { useUserNodes } from '../nodes';
import { useZones } from '../zones';

// Types based on backend serializers
interface CountByCrop {
    fallow: number;
    corn?: number;
    soy?: number;
    wheat?: number;
    barley?: number;
}

interface YieldByGrower {
    avgYield: number;
    growerId: number | null;
    zoneShortName: string;
    email?: string;
}

interface YieldByField {
    avgYield: number;
    field: {
        id: number;
        name: string;
    };
    zoneShortName: string;
}

export interface UserNodeFieldsDashboardResponse {
    count?: number;
    totalArea?: number;
    countByCrop?: CountByCrop;
    countByType?: {
        common: number;
        premium: number;
    };
    withFieldview?: number;
    withRecommendations?: number;
}

interface UserNodeGrowersDashboardResponse {
    count: number;
    withFieldview: number;
    countByType?: {
        common: number;
        premium: number;
    };
}

interface CountByType {
    corn: number;
    soy: number;
    funguicide: number;
    insecticide: number;
    herbicide: number;
    density: number;
}

interface HarvestReportDashboard {
    areaByCrop: Pick<CountByCrop, 'corn' | 'soy' | 'wheat' | 'barley'>;
    avgYieldByCrop: Pick<CountByCrop, 'corn' | 'soy' | 'wheat' | 'barley'>;
    countByCrop: Pick<CountByCrop, 'corn' | 'soy' | 'wheat' | 'barley'>;
    avgYieldByProducts: YieldByProduct[];
    avgYieldByGrowers: YieldByGrower[];
    avgYieldByFields?: YieldByField[];
    growersByCrop: Pick<CountByCrop, 'corn' | 'soy' | 'wheat' | 'barley'>;
}

interface YieldByProduct {
    avgYield: number;
    productName: string;
    zoneShortName: string;
}

export interface UserNodeReportsDashboardResponse {
    harvest: HarvestReportDashboard;
    count: number;
}

export interface UserNodeRecommendationsDashboardResponse {
    count?: number;
    countByType?: CountByType;
    mostRecommendedProducts?: Array<{
        name: string;
        count: number;
        type: string;
        productProfileId?: number;
        productId?: number;
        cropProtectionProductId?: number;
    }>;
    fieldsCount?: number;
}

export interface UserNodeFieldsMostRecommendedProductsResponse {
    mostRecommendedProducts: Array<{
        name: string;
        count: number;
        type: string;
        productProfileId?: number;
        productId?: number;
        cropProtectionProductId?: number;
    }>;
}

interface NextVisitDescription {
    es: string;
    en: string;
    pt: string;
}

interface UserNodeNextVisitDashboardResponse {
    nextVisit: string; // date format
    cropMonitoring: number;
    description: NextVisitDescription;
}

// Types for fields-by-active-stage endpoint
interface CornCountByStage {
    fallow: number;
    planting: number;
    emergence: number;
    flowering: number;
    maturity: number;
    harvested: number;
}

interface NonCornCountByStage {
    fallow: number;
    planting: number;
    harvested: number;
}

export interface UserNodeFieldsByActiveStageDashboardResponse {
    countByStage: CornCountByStage | NonCornCountByStage;
}

export interface UserNodeVisitReportsDashboardResponse {
    count: number;
    countByStatus: {
        draft: number;
        saved: number;
    };
}

export interface UserNodeActiveCampaignDashboardResponse {
    count: number;
    withActiveCampaign: number;
    withoutActiveCampaign: number;
}

// API Providers
const getFieldsDashboardFromApi = async ({
    country,
    includeChildren = true,
    node,
    app_instance,
    select,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    app_instance?: number;
    select?: string[];
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeFieldsDashboardResponse> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/dashboards/fields/`, window.location.origin);

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (app_instance) params.append('app_instance', String(app_instance));
    if (select && select.length > 0) params.append('select', select.join(','));
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getGrowersDashboardFromApi = async ({
    includeChildren = true,
    node,
    excludeTargetNode = false,
    nodeType,
}: {
    includeChildren?: boolean;
    node?: number;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeGrowersDashboardResponse> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/dashboards/growers/`, window.location.origin);

    const params = new URLSearchParams();
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getFieldsByActiveStageDashboardFromApi = async ({
    country,
    includeChildren = true,
    node,
    campaign,
    crop,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    campaign?: number;
    crop: CropType;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeFieldsByActiveStageDashboardResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/fields/by-active-stage/`,
        window.location.origin
    );

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (campaign) params.append('campaign', String(campaign));
    if (nodeType) params.append('node_type', nodeType);
    params.append('crop', crop);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getRecommendationsDashboardFromApi = async ({
    country,
    includeChildren = true,
    node,
    app_instance,
    year,
    type_in,
    select,
    brand,
    has_glifosato,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    app_instance?: number;
    year?: number;
    type_in?: string[];
    select?: ('base_stats' | 'most_recommended_products')[];
    brand?: number;
    has_glifosato?: boolean;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeRecommendationsDashboardResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/recommendations/`,
        window.location.origin
    );

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (app_instance) params.append('app_instance', String(app_instance));
    if (year) params.append('year', String(year));
    if (type_in && type_in.length > 0) params.append('type__in', type_in.join(','));
    if (select && select.length > 0) params.append('select', select.join(','));
    if (brand) params.append('brand', String(brand));
    if (has_glifosato !== undefined) params.append('has_glifosato', String(has_glifosato));
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getFieldsMostRecommendedProductsFromApi = async ({
    country,
    includeChildren = true,
    node,
    app_instance,
    year,
    type_in,
    brand,
    has_glifosato,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    app_instance?: number;
    year?: number;
    type_in?: string[];
    brand?: number;
    has_glifosato?: boolean;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeFieldsMostRecommendedProductsResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/fields/most-recommended-products/`,
        window.location.origin
    );

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (app_instance) params.append('app_instance', String(app_instance));
    if (year) params.append('year', String(year));
    if (type_in && type_in.length > 0) params.append('type__in', type_in.join(','));
    if (brand) params.append('brand', String(brand));
    if (has_glifosato !== undefined) params.append('has_glifosato', String(has_glifosato));
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

export const getGrowersDashboardReportsFromApi = async ({
    country,
    appInstance,
    year,
    node,
    crop,
    includeChildren = true,
    excludeTargetNode = false,
    nodeType,
}: {
    country: string;
    appInstance: number;
    year: number;
    node?: number;
    crop?: CropType;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeReportsDashboardResponse> => {
    const apiUrl = new URL(`${(settings as any).urls.apiDomain}/core/v1/dashboards/reports/`, window.location.origin);
    const params = new URLSearchParams();
    if (country) params.append('country', country);
    if (node) params.append('node', String(node));
    if (appInstance) params.append('app_instance', String(appInstance));
    if (crop) params.append('crop', crop);
    if (includeChildren !== undefined) params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (nodeType) params.append('node_type', nodeType);
    params.append('campaign', String(year));
    apiUrl.search = params.toString();
    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getNextVisitDashboardFromApi = async ({ id }: { id: number }): Promise<UserNodeNextVisitDashboardResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/next-visit/${id}/`,
        window.location.origin
    );

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getVisitReportsDashboardFromApi = async ({
    country,
    includeChildren = true,
    node,
    status,
    date,
    campaign,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    status?: 'draft' | 'saved';
    date?: string;
    campaign?: number;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeVisitReportsDashboardResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/visit-reports/`,
        window.location.origin
    );

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (status) params.append('status', status);
    if (date) params.append('date', date);
    if (campaign) params.append('campaign', String(campaign));
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

const getActiveCampaignDashboardFromApi = async ({
    country,
    includeChildren = true,
    node,
    campaign,
    crop,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    campaign?: number;
    crop?: CropType;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}): Promise<UserNodeActiveCampaignDashboardResponse> => {
    const apiUrl = new URL(
        `${(settings as any).urls.apiDomain}/core/v1/dashboards/active-campaign/`,
        window.location.origin
    );

    const params = new URLSearchParams();
    if (country) params.append('country', country);
    params.append('include_children', String(includeChildren));
    if (excludeTargetNode !== undefined) params.append('exclude_target_node', String(excludeTargetNode));
    if (node) params.append('node', String(node));
    if (campaign) params.append('campaign', String(campaign));
    if (crop) params.append('crop', crop);
    if (nodeType) params.append('node_type', nodeType);
    apiUrl.search = params.toString();

    const res = await axFetch.get(apiUrl.toString());
    return res.data;
};

// Hooks
export const useFieldsDashboard = ({
    country,
    includeChildren = true,
    node,
    select,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    select?: string[];
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const appInstance = useSelector(getAppInstance);
    const key = {
        key: 'fieldsDashboard',
        country,
        includeChildren,
        node,
        app_instance: appInstance?.id,
        select,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(key, getFieldsDashboardFromApi);
};

export const useGrowersDashboard = ({
    includeChildren = true,
    node,
    excludeTargetNode = false,
    nodeType,
}: {
    includeChildren?: boolean;
    node?: number;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const key = {
        key: 'growersDashboard',
        includeChildren,
        node,
        excludeTargetNode,
        nodeType,
    };
    return useSWR(key, getGrowersDashboardFromApi);
};

export const useRVDashboards = ({ userNodeId }: { userNodeId: number }) => {
    const appInstance = useSelector(getAppInstance);

    // Get all RV user nodes
    const rvsUserNodesQuery = useUserNodes({
        page_size: 10000,
        page: 1,
        node: userNodeId,
        type: 'rv',
        excludeTargetNode: true,
    });

    // Extract userNodeIds from the results
    const rvUserNodeIds = useMemo(() => {
        return rvsUserNodesQuery.data?.results?.map((userNode) => userNode.id) || [];
    }, [rvsUserNodesQuery.data]);

    // Fetch growers and fields dashboards for each RV user node
    const key = useMemo(
        () =>
            rvUserNodeIds.length > 0 && !rvsUserNodesQuery.isValidating
                ? {
                      key: 'rvdashboards',
                      rvUserNodeIds: [...rvUserNodeIds].sort((a, b) => a - b), // Sort for consistent cache key
                      app_instance: appInstance?.id,
                      excludeTargetNode: true,
                  }
                : null,
        [rvUserNodeIds, rvsUserNodesQuery.isValidating, appInstance?.id]
    );

    const fetchAllRVDashboards = async () => {
        if (!key || rvUserNodeIds.length === 0) {
            return [];
        }

        // Fetch all growers and fields dashboards in parallel
        const dashboardPromises = rvUserNodeIds.map(async (rvUserNodeId) => {
            const [growersDashboard, fieldsDashboard] = await Promise.all([
                getGrowersDashboardFromApi({
                    node: rvUserNodeId,
                    nodeType: 'grower',
                    excludeTargetNode: true,
                }),
                getFieldsDashboardFromApi({
                    node: rvUserNodeId,
                    select: ['base_stats'],
                    app_instance: appInstance?.id,
                    excludeTargetNode: true,
                }),
            ]);

            return {
                userNodeId: rvUserNodeId,
                growersDashboard,
                fieldsDashboard,
            };
        });

        const dashboardResults = await Promise.all(dashboardPromises);

        return dashboardResults;
    };

    const swrResult = useSWR(key, fetchAllRVDashboards, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });

    // Calculate aggregated totals
    const aggregatedData = useMemo(() => {
        if (!swrResult.data) return undefined;

        const totalCount = swrResult.data.reduce((acc, item) => acc + (item.growersDashboard?.count || 0), 0);
        const totalWithFieldview = swrResult.data.reduce(
            (acc, item) => acc + (item.growersDashboard?.withFieldview || 0),
            0
        );

        // Aggregate fields dashboard stats
        const totalFieldsCount = swrResult.data.reduce((acc, item) => acc + (item.fieldsDashboard?.count || 0), 0);
        const totalFieldsArea = swrResult.data.reduce((acc, item) => acc + (item.fieldsDashboard?.totalArea || 0), 0);

        return {
            growersByRv: swrResult.data.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.growersDashboard?.count || 0,
                withFieldview: item.growersDashboard?.withFieldview || 0,
            })),
            fieldsByRv: swrResult.data.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.fieldsDashboard?.count || 0,
                totalArea: item.fieldsDashboard?.totalArea || 0,
            })),
            totalCount,
            totalWithFieldview,
            totalFieldsCount,
            totalFieldsArea,
        };
    }, [swrResult.data]);

    const isLoading = (!swrResult.data && swrResult.isValidating) || rvsUserNodesQuery.isValidating;

    return {
        ...swrResult,
        data: aggregatedData,
        isLoading,
        isValidating: rvsUserNodesQuery.isValidating || swrResult.isValidating,
    };
};

export const useRecommendationsDashboard = (
    {
        includeChildren = true,
        node,
        year,
        type_in,
        select,
        brand,
        has_glifosato,
        excludeTargetNode = false,
        nodeType,
    }: {
        includeChildren?: boolean;
        node?: number;
        year?: number;
        type_in?: string[];
        select?: ('base_stats' | 'most_recommended_products')[];
        brand?: number;
        has_glifosato?: boolean;
        excludeTargetNode?: boolean;
        nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const appInstance = useSelector(getAppInstance);
    const key = {
        key: 'recommendationsDashboard',
        includeChildren,
        node,
        app_instance: appInstance?.id,
        year,
        type_in,
        select,
        brand,
        has_glifosato,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(enabled ? key : null, getRecommendationsDashboardFromApi);
};

export const useFieldsMostRecommendedProducts = (
    {
        includeChildren = true,
        node,
        year,
        type_in,
        brand,
        has_glifosato,
        excludeTargetNode = false,
        nodeType,
    }: {
        includeChildren?: boolean;
        node?: number;
        year?: number;
        type_in?: string[];
        brand?: number;
        has_glifosato?: boolean;
        excludeTargetNode?: boolean;
        nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
    },
    { enabled = true }: { enabled?: boolean } = { enabled: true }
) => {
    const appInstance = useSelector(getAppInstance);
    const key = {
        key: 'fieldsMostRecommendedProducts',
        includeChildren,
        node,
        app_instance: appInstance?.id,
        year,
        type_in,
        brand,
        has_glifosato,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(enabled ? key : null, getFieldsMostRecommendedProductsFromApi);
};

export const useRVRecommendationsDashboard = ({
    userNodeId,
    includeChildren = true,
    year,
    type_in,
    select,
    excludeTargetNode = false,
}: {
    userNodeId: number;
    includeChildren?: boolean;
    year?: number;
    type_in?: string[];
    select?: ('base_stats' | 'most_recommended_products')[];
    excludeTargetNode?: boolean;
}) => {
    const appInstance = useSelector(getAppInstance);

    // Get all RV user nodes
    const rvsUserNodesQuery = useUserNodes({
        page_size: 10000,
        page: 1,
        node: userNodeId,
        type: 'rv',
        excludeTargetNode,
    });

    // Extract userNodeIds from the results
    const rvUserNodeIds = useMemo(() => {
        return rvsUserNodesQuery.data?.results?.map((userNode) => userNode.id) || [];
    }, [rvsUserNodesQuery.data]);

    // Fetch recommendations dashboards for each RV user node
    const key = useMemo(
        () =>
            rvUserNodeIds.length > 0 && !rvsUserNodesQuery.isValidating
                ? {
                      key: 'rvRecommendationsDashboard',
                      rvUserNodeIds: [...rvUserNodeIds].sort((a, b) => a - b), // Sort for consistent cache key
                      app_instance: appInstance?.id,
                      includeChildren,
                      year,
                      type_in,
                      select,
                      excludeTargetNode,
                  }
                : null,
        [
            rvUserNodeIds,
            rvsUserNodesQuery.isValidating,
            appInstance?.id,
            includeChildren,
            year,
            type_in,
            select,
            excludeTargetNode,
        ]
    );

    const fetchAllRVRecommendations = async () => {
        if (!key || rvUserNodeIds.length === 0) {
            return [];
        }

        // Fetch all recommendations dashboards in parallel
        const recommendationsPromises = rvUserNodeIds.map(async (rvUserNodeId) => {
            const recommendationsDashboard = await getRecommendationsDashboardFromApi({
                node: rvUserNodeId,
                includeChildren,
                app_instance: appInstance?.id,
                year,
                type_in,
                select,
                excludeTargetNode,
            });

            return {
                userNodeId: rvUserNodeId,
                recommendationsDashboard,
            };
        });

        const recommendationsResults = await Promise.all(recommendationsPromises);

        return recommendationsResults;
    };

    const swrResult = useSWR(key, fetchAllRVRecommendations, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });

    // Calculate aggregated totals
    const aggregatedData = useMemo(() => {
        if (!swrResult.data) return undefined;

        // Aggregate total count
        const totalCount = swrResult.data.reduce((acc, item) => acc + (item.recommendationsDashboard?.count || 0), 0);

        // Aggregate countByType
        const aggregatedCountByType: CountByType = {
            corn: 0,
            soy: 0,
            funguicide: 0,
            insecticide: 0,
            herbicide: 0,
            density: 0,
        };

        swrResult.data.forEach((item) => {
            const countByType = item.recommendationsDashboard?.countByType;
            if (countByType) {
                aggregatedCountByType.corn += countByType.corn || 0;
                aggregatedCountByType.soy += countByType.soy || 0;
                aggregatedCountByType.funguicide += countByType.funguicide || 0;
                aggregatedCountByType.insecticide += countByType.insecticide || 0;
                aggregatedCountByType.herbicide += countByType.herbicide || 0;
                aggregatedCountByType.density += countByType.density || 0;
            }
        });

        // Aggregate mostRecommendedProducts
        const productsMap = new Map<
            string,
            {
                name: string;
                count: number;
                type: string;
                productProfileId?: number;
                productId?: number;
                cropProtectionProductId?: number;
            }
        >();

        swrResult.data.forEach((item) => {
            const products = item.recommendationsDashboard?.mostRecommendedProducts || [];
            products.forEach((product) => {
                const key = `${product.name}-${product.type}`;
                const existing = productsMap.get(key);
                if (existing) {
                    existing.count += product.count;
                } else {
                    productsMap.set(key, { ...product });
                }
            });
        });

        const aggregatedMostRecommendedProducts = Array.from(productsMap.values()).sort((a, b) => b.count - a.count);

        // Aggregate fieldsCount
        const totalFieldsCount = swrResult.data.reduce(
            (acc, item) => acc + (item.recommendationsDashboard?.fieldsCount || 0),
            0
        );

        return {
            recommendationsByRv: swrResult.data.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.recommendationsDashboard?.count || 0,
                countByType: item.recommendationsDashboard?.countByType,
                mostRecommendedProducts: item.recommendationsDashboard?.mostRecommendedProducts || [],
                fieldsCount: item.recommendationsDashboard?.fieldsCount || 0,
            })),
            totalCount,
            aggregatedCountByType,
            aggregatedMostRecommendedProducts,
            totalFieldsCount,
        };
    }, [swrResult.data]);

    const isLoading = (!swrResult.data && swrResult.isValidating) || rvsUserNodesQuery.isValidating;

    return {
        ...swrResult,
        data: aggregatedData,
        isLoading,
        isValidating: rvsUserNodesQuery.isValidating || swrResult.isValidating,
    };
};

export const useRVVisitReportsDashboard = ({
    userNodeId,
    includeChildren = true,
    status,
    date,
    campaign,
    excludeTargetNode = false,
}: {
    userNodeId: number;
    includeChildren?: boolean;
    status?: 'draft' | 'saved';
    date?: string;
    campaign?: number;
    excludeTargetNode?: boolean;
}) => {
    const appInstance = useSelector(getAppInstance);
    const country = useSelector(getSessionCountry);

    // Get all RV user nodes
    const rvsUserNodesQuery = useUserNodes({
        page_size: 10000,
        page: 1,
        node: userNodeId,
        type: 'rv',
        excludeTargetNode,
    });

    // Extract userNodeIds from the results
    const rvUserNodeIds = useMemo(() => {
        return rvsUserNodesQuery.data?.results?.map((userNode) => userNode.id) || [];
    }, [rvsUserNodesQuery.data]);

    // Fetch visit reports dashboards for each RV user node
    const key = useMemo(
        () =>
            rvUserNodeIds.length > 0 && !rvsUserNodesQuery.isValidating
                ? {
                      key: 'rvVisitReportsDashboard',
                      rvUserNodeIds: [...rvUserNodeIds].sort((a, b) => a - b), // Sort for consistent cache key
                      app_instance: appInstance?.id,
                      country: country?.code,
                      includeChildren,
                      status,
                      date,
                      campaign,
                      excludeTargetNode,
                  }
                : null,
        [
            rvUserNodeIds,
            rvsUserNodesQuery.isValidating,
            appInstance?.id,
            country?.code,
            includeChildren,
            status,
            date,
            campaign,
            excludeTargetNode,
        ]
    );

    const fetchAllRVVisitReports = async () => {
        if (!key || rvUserNodeIds.length === 0) {
            return [];
        }

        // Fetch all visit reports dashboards in parallel
        const visitReportsPromises = rvUserNodeIds.map(async (rvUserNodeId) => {
            const visitReportsDashboard = await getVisitReportsDashboardFromApi({
                node: rvUserNodeId,
                includeChildren,
                country: country?.code,
                status,
                date,
                campaign,
                excludeTargetNode,
            });

            return {
                userNodeId: rvUserNodeId,
                visitReportsDashboard,
            };
        });

        const visitReportsResults = await Promise.all(visitReportsPromises);

        return visitReportsResults;
    };

    const swrResult = useSWR(key, fetchAllRVVisitReports, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });

    // Calculate aggregated totals
    const aggregatedData = useMemo(() => {
        if (!swrResult.data) return undefined;

        // Aggregate total count
        const totalCount = swrResult.data.reduce((acc, item) => acc + (item.visitReportsDashboard?.count || 0), 0);

        // Aggregate countByStatus
        const aggregatedCountByStatus = {
            draft: 0,
            saved: 0,
        };

        swrResult.data.forEach((item) => {
            const countByStatus = item.visitReportsDashboard?.countByStatus;
            if (countByStatus) {
                aggregatedCountByStatus.draft += countByStatus.draft || 0;
                aggregatedCountByStatus.saved += countByStatus.saved || 0;
            }
        });

        return {
            visitReportsByRv: swrResult.data.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.visitReportsDashboard?.count || 0,
                countByStatus: item.visitReportsDashboard?.countByStatus,
            })),
            totalCount,
            aggregatedCountByStatus,
        };
    }, [swrResult.data]);

    const isLoading = (!swrResult.data && swrResult.isValidating) || rvsUserNodesQuery.isValidating;

    return {
        ...swrResult,
        data: aggregatedData,
        isLoading,
        isValidating: rvsUserNodesQuery.isValidating || swrResult.isValidating,
    };
};

export const useRVActiveCampaignDashboard = ({
    userNodeId,
    includeChildren = true,
    campaign,
    crop,
    excludeTargetNode = false,
}: {
    userNodeId: number;
    includeChildren?: boolean;
    campaign?: number;
    crop?: CropType;
    excludeTargetNode?: boolean;
}) => {
    const appInstance = useSelector(getAppInstance);
    const country = useSelector(getSessionCountry);

    // Get all RV user nodes
    const rvsUserNodesQuery = useUserNodes({
        page_size: 10000,
        page: 1,
        node: userNodeId,
        type: 'rv',
        excludeTargetNode,
    });

    // Extract userNodeIds from the results
    const rvUserNodeIds = useMemo(() => {
        return rvsUserNodesQuery.data?.results?.map((userNode) => userNode.id) || [];
    }, [rvsUserNodesQuery.data]);

    // Fetch active campaign dashboards for each RV user node
    const key = useMemo(
        () =>
            rvUserNodeIds.length > 0 && !rvsUserNodesQuery.isValidating
                ? {
                      key: 'rvActiveCampaignDashboard',
                      rvUserNodeIds: [...rvUserNodeIds].sort((a, b) => a - b), // Sort for consistent cache key
                      app_instance: appInstance?.id,
                      country: country?.code,
                      includeChildren,
                      campaign,
                      crop,
                      excludeTargetNode,
                  }
                : null,
        [
            rvUserNodeIds,
            rvsUserNodesQuery.isValidating,
            appInstance?.id,
            country?.code,
            includeChildren,
            campaign,
            crop,
            excludeTargetNode,
        ]
    );

    const fetchAllRVActiveCampaigns = async () => {
        if (!key || rvUserNodeIds.length === 0) {
            return [];
        }

        // Fetch all active campaign dashboards in parallel
        const activeCampaignPromises = rvUserNodeIds.map(async (rvUserNodeId) => {
            const activeCampaignDashboard = await getActiveCampaignDashboardFromApi({
                node: rvUserNodeId,
                includeChildren,
                country: country?.code,
                campaign,
                crop,
                excludeTargetNode,
            });

            return {
                userNodeId: rvUserNodeId,
                activeCampaignDashboard,
            };
        });

        const activeCampaignResults = await Promise.all(activeCampaignPromises);

        return activeCampaignResults;
    };

    const swrResult = useSWR(key, fetchAllRVActiveCampaigns, {
        revalidateIfStale: false,
        revalidateOnFocus: false,
    });

    // Calculate aggregated totals
    const aggregatedData = useMemo(() => {
        if (!swrResult.data) return undefined;

        // Aggregate total count
        const totalCount = swrResult.data.reduce((acc, item) => acc + (item.activeCampaignDashboard?.count || 0), 0);

        // Aggregate withActiveCampaign and withoutActiveCampaign
        const aggregatedWithActiveCampaign = swrResult.data.reduce(
            (acc, item) => acc + (item.activeCampaignDashboard?.withActiveCampaign || 0),
            0
        );

        const aggregatedWithoutActiveCampaign = swrResult.data.reduce(
            (acc, item) => acc + (item.activeCampaignDashboard?.withoutActiveCampaign || 0),
            0
        );

        return {
            activeCampaignsByRv: swrResult.data.map((item) => ({
                userNodeId: item.userNodeId,
                count: item.activeCampaignDashboard?.count || 0,
                withActiveCampaign: item.activeCampaignDashboard?.withActiveCampaign || 0,
                withoutActiveCampaign: item.activeCampaignDashboard?.withoutActiveCampaign || 0,
            })),
            totalCount,
            aggregatedWithActiveCampaign,
            aggregatedWithoutActiveCampaign,
        };
    }, [swrResult.data]);

    const isLoading = (!swrResult.data && swrResult.isValidating) || rvsUserNodesQuery.isValidating;

    return {
        ...swrResult,
        data: aggregatedData,
        isLoading,
        isValidating: rvsUserNodesQuery.isValidating || swrResult.isValidating,
    };
};

export const useNextVisitDashboard = ({ id }: { id: number }) => {
    const key = {
        key: 'nextVisitDashboard',
        id,
    };
    return useSWRImmutable(key, getNextVisitDashboardFromApi);
};

export const useGrowersDashboardReports = ({
    year,
    node,
    crop,
    includeChildren = true,
    excludeTargetNode = false,
    nodeType,
}: {
    year: number;
    node?: number;
    crop?: CropType;
    includeChildren?: boolean;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const country = useSelector(getSessionCountry);
    const appInstance = useSelector(getAppInstance);
    const growersQuery = useUserNodes({
        page: 1,
        page_size: 10000,
        node,
        includeChildren,
        excludeTargetNode,
    });

    const key = {
        key: 'growersDashboardReports',
        country: country?.code ?? '',
        year,
        node,
        appInstance: appInstance?.id,
        crop,
        includeChildren,
        excludeTargetNode,
        nodeType,
    };
    const reportsWithGrowersNamesQuery = useSWRImmutable(key, getGrowersDashboardReportsFromApi);

    const parsedData = useMemo(() => {
        if (!reportsWithGrowersNamesQuery.data || !growersQuery.data) return undefined;
        return {
            ...reportsWithGrowersNamesQuery.data,
            harvest: {
                ...reportsWithGrowersNamesQuery.data?.harvest,
                avgYieldByGrowers: reportsWithGrowersNamesQuery.data?.harvest.avgYieldByGrowers.map((growerYield) => {
                    const growerData = growersQuery.data?.results.find((g) => g.id === growerYield.growerId);
                    return {
                        ...growerYield,
                        grower: growerData,
                    };
                }),
            },
        };
    }, [reportsWithGrowersNamesQuery.data, growersQuery.data]);

    // Get Zones
    const zonesQuery = useZones({ type: 'corn' });

    const zones = useMemo(() => {
        const countryZones = zonesQuery.data?.filter((zone: any) => zone.country === country.code) || [];
        const uniqueZones = Array.from(new Set(countryZones.map((zone: any) => _(zone.shortName))))
            .map((zone) => {
                return {
                    value: zone,
                    isDisabled: !parsedData?.harvest.avgYieldByGrowers?.some(
                        (grower) => _(grower.zoneShortName) === _(zone)
                    ),
                };
            })
            .sort((a, b) => (a.isDisabled === b.isDisabled ? 0 : a.isDisabled ? 1 : -1));

        return uniqueZones;
    }, [zonesQuery.data, country.code, parsedData]);
    const avaiableZones = useMemo(() => {
        return zones.filter((zone) => !zone.isDisabled);
    }, [zones]);

    return { ...reportsWithGrowersNamesQuery, data: { ...parsedData, zones, avaiableZones } };
};

export const useFieldsByStageDashboard = ({
    country,
    includeChildren = true,
    node,
    campaign,
    crop,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    campaign?: number;
    crop: CropType;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const key = {
        key: 'fieldsByStageDashboard',
        country,
        includeChildren,
        campaign,
        crop,
        node,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(key, getFieldsByActiveStageDashboardFromApi);
};

export const useVisitReportsDashboard = ({
    country,
    includeChildren = true,
    node,
    status,
    date,
    campaign,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    status?: 'draft' | 'saved';
    date?: string;
    campaign?: number;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const key = {
        key: 'visitReportsDashboard',
        country,
        includeChildren,
        node,
        status,
        date,
        campaign,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(key, getVisitReportsDashboardFromApi);
};

export const useActiveCampaignDashboard = ({
    country,
    includeChildren = true,
    node,
    campaign,
    crop,
    excludeTargetNode = false,
    nodeType,
}: {
    country?: string;
    includeChildren?: boolean;
    node?: number;
    campaign?: number;
    crop?: CropType;
    excludeTargetNode?: boolean;
    nodeType?: 'rv' | 'grower' | 'consultant' | 'other';
}) => {
    const key = {
        key: 'activeCampaignDashboard',
        country,
        includeChildren,
        node,
        campaign,
        crop,
        excludeTargetNode,
        nodeType,
    };
    return useSWRImmutable(key, getActiveCampaignDashboardFromApi);
};
